/**
 * Viewer mode router — ONE layout, three modes:
 *
 *   browse   full-bleed map + right panel (Cars tab active)
 *   detail   map shrunk by the rail width + right panel on the Driver
 *            tab with VIDEO in the media slot
 *   theater  full-bleed video + rail whose tabs switch LOCALLY
 *            (TheaterMode.tsx)
 *
 * browse ↔ detail share ONE mounted MapShell so Leaflet never remounts
 * on a pin click — the canvas just animates its right edge by the rail
 * width (mockup .g-canvas.detail) and gets an invalidateSize kick via
 * MapCanvas's resizeKey.
 *
 * `ViewerController` is the orchestrator's full API surface — built
 * once in BrowseStateView and threaded to every mode component.
 */

import React from 'react';
import { Map as MapIcon, Tv } from 'lucide-react';

import { cn } from '@/lib/utils';
import { EventRow, Lap, StageRouteGeometry } from '../../services/fleet';
import { GpsSummary } from '../../types';
import { EventLiveDevice } from '../../hooks/useEventLiveDevices';
import { LG_MEDIA_QUERY, useMediaQuery } from '../../hooks/useMediaQuery';
import { GlassRailData } from '../PlayerChrome';
import { V2PlayerHandle } from './V2Player';
import BrowseMode from './BrowseMode';
import DetailMode from './DetailMode';
import TheaterMode from './TheaterMode';
import FilterStrip, { Filter } from './FilterStrip';
import { DriverSessionRow } from './SessionSelector';
import type { StageOption } from './StageStrip';
import MapCanvas, { MapModules } from './MapCanvas';
import RightPanel from './RightPanel';
import MobileViewer from './MobileViewer';
import { CarsDensity } from './CarsTab';
import { CompareState } from './DriverTab';
import { EntryIntent } from './entryIntent';
import { RecordingStatus } from './recordingStatus';
import { StageEventEntry } from './StageEvents';
import { Standing } from './useEventStandings';
import { VideoBadge } from './FeedPlayer';
import { LagInfo } from './lag';
import { ViewerMode } from './urlState';

export interface ViewerController {
  event: EventRow;
  replay: boolean;
  isCircuit: boolean;
  mode: ViewerMode;

  // roster
  devices: EventLiveDevice[];
  filtered: EventLiveDevice[];
  liveCount: number;
  filter: Filter;
  setFilter: (f: Filter) => void;

  // selection / navigation (URL-backed)
  selected: EventLiveDevice | null;
  selectCar: (id: string, mode?: ViewerMode) => void;
  goBrowse: () => void;
  goDetail: () => void;
  goTheater: () => void;

  // stage selector (URL-backed via ?stage=)
  stageOptions: StageOption[];
  /** False on single-stage events — no strip anywhere. */
  stageStripVisible: boolean;
  selectedStage: number | null;
  setStage: (index: number | null) => void;
  /** device_id → session_id to play for the selected stage. */
  stageSessionByDevice: Map<string, string>;
  /** 'SS2' / 'H1' for the selected stage; null when All. */
  selectedStageLabel: string | null;
  /** Official corridor geometry for every stage — the map overlay. */
  stageRoutes: StageRouteGeometry[];

  // session surfacer (SessionSelector — per-car recordings list)
  /** Selected car's sessions, newest first, stage-tagged. */
  driverSessions: DriverSessionRow[];
  /** Mount a session as VOD; the live session's row returns to live. */
  handlePickSession: (sessionId: string) => void;

  // cars-list density (localStorage-backed, shared across modes)
  density: CarsDensity;
  setDensity: (d: CarsDensity) => void;

  // follow (localStorage-persisted per event)
  followedIds: Set<string>;
  toggleFollow: (id: string) => void;

  // stream-health
  lagFor: (d: EventLiveDevice) => LagInfo;
  playerLagInfo: LagInfo | null;
  onPlayerEdge: (videoEndUtcMs: number | null) => void;

  // map
  modules: MapModules | null;
  center: [number, number];
  trailTrack: GpsSummary['track'] | null;
  trailCoverage: Array<[number, number]> | null;
  playheadUtc: number | null;
  handleSeek: (utcMs: number) => void;
  racelineCoords: Array<[number, number]> | null;
  stageProgress: { pct: number; sM: number } | null;
  stageLengthM: number | null;

  // player
  playerRef: React.MutableRefObject<V2PlayerHandle | null>;
  handlePlayheadUtc: (utcMs: number | null) => void;
  /** First PROGRAM-DATE-TIME of the mounted PRIMARY player's timeline; null
   *  before it's known. Clamps the Speed/IMU charts' left edge to where
   *  video actually starts instead of the GPS trail's earlier pre-roll. A
   *  secondary/compare player must never write this. */
  videoStartMs: number | null;
  handlePdtReady: (utcMs: number) => void;
  lapOverride: { sessionId: string; utcMs: number } | null;
  /** Player initial seek on mount: a lap-click seek (`entryIntent`
   *  'explicit'), else the saved resume position for the mounted session
   *  ('resume'), or always null for 'live' — a live entry never applies a
   *  saved position; the player places itself at the live edge instead.
   *  Null's meaning is read together with `entryIntent`, never alone. */
  initialSeekUtcMs: number | null;
  /** Why the mounted session is opening where it is — see `entryIntent.ts`.
   *  Threaded straight to FeedPlayer/V2Player at every host boundary. */
  entryIntent: EntryIntent;
  mountedSessionId: string | null;
  /** Session id + live-pick epoch — the stage player's mount key. */
  mountedPlayerKey: string;
  videoBadge: VideoBadge | null;
  playerVod: boolean;
  /** Truthful status of the mounted session (recording / uploads still
   *  recovering / media complete), from the PRIMARY player's own coverage —
   *  same "keyed on the mounted session, not the selected car" convention
   *  as `videoStartMs`. Null before any coverage verdict has arrived. */
  recordingStatus: RecordingStatus | null;
  /** Wire the primary FeedPlayer's coverage-derived status into
   *  `recordingStatus` above. */
  onRecordingStatus: (status: RecordingStatus | null) => void;
  /** Fullscreen-landscape glass rail cells. */
  glassRail: GlassRailData | null;
  /** Speed (km/h) at the current playhead — replay-aware; shared by the
   *  glass rail and the Driver-tab Speed cell. Null = no reading. */
  playbackSpeedKmh: number | null;

  // laps
  laps: Lap[];
  /** Circuit best-lap standings keyed by device_id (empty for rally). */
  standings: Map<string, Standing>;
  selectedLapIdx: number | null;
  handlePickLap: (lap: Lap, idx: number) => void;
  handlePickLive: () => void;

  // compare
  compareCapable: boolean;
  compareBusy: boolean;
  handleCompareOpen: () => void;
  compareRun: CompareState | null;
  compareePlayerRef: React.MutableRefObject<V2PlayerHandle | null>;
  handleCompareClose: () => void;

  // stage events
  stageEvents: StageEventEntry[];

  // mobile (<lg) panel overlay in browse mode
  mobilePanelOpen: boolean;
  setMobilePanelOpen: (open: boolean) => void;
}

/** Top-right Map | Theater pill (mockup .map-toggle). */
export const ModeToggle: React.FC<{
  mode: 'browse' | 'detail' | 'theater';
  hasCar: boolean;
  onBrowse: () => void;
  onTheater: () => void;
  className?: string;
}> = ({ mode, hasCar, onBrowse, onTheater, className }) => (
  <div
    className={cn(
      'flex overflow-hidden rounded-md border border-white/15 bg-black/70 backdrop-blur',
      className,
    )}
  >
    <button
      type="button"
      onClick={onBrowse}
      title="Map browse"
      className={cn(
        'inline-flex items-center gap-1 px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wide',
        mode === 'browse' ? 'bg-accent font-bold text-fg-inverse' : 'text-white hover:text-accent',
      )}
    >
      <MapIcon className="size-3.5" />
      Map
    </button>
    <button
      type="button"
      onClick={onTheater}
      disabled={!hasCar}
      title={hasCar ? 'Theater mode' : 'Pick a car first'}
      className={cn(
        'inline-flex items-center gap-1 px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wide',
        mode === 'theater'
          ? 'bg-accent font-bold text-fg-inverse'
          : hasCar
            ? 'text-white hover:text-accent'
            : 'cursor-default text-white/40',
      )}
    >
      <Tv className="size-3.5" />
      Theater
    </button>
  </div>
);

/** Stream-health legend (mockup .map-legend), bottom-right glass pill.
 *  Fan-facing copy — "Fresh <2s / Lagging / Buffer / Stale" is internal
 *  stream-health vocabulary (marketing-voice rule: gloss insider terms). */
const MapLegend: React.FC<{ className?: string }> = ({ className }) => (
  <div
    className={cn(
      'pointer-events-none inline-flex items-center gap-2.5 rounded-full border border-white/10 bg-black/70 px-2.5 py-1.5 text-[10px] text-[#ccc] backdrop-blur',
      className,
    )}
  >
    <span className="inline-flex items-center gap-1 whitespace-nowrap">
      <i className="inline-block h-[7px] w-[7px] rounded-full bg-status-online shadow-[0_0_6px_var(--status-online)]" />
      Live now
    </span>
    <span className="inline-flex items-center gap-1 whitespace-nowrap">
      <i className="inline-block h-[7px] w-[7px] rounded-full bg-status-lagging" />
      Behind
    </span>
    <span className="inline-flex items-center gap-1 whitespace-nowrap">
      <i className="inline-block h-[7px] w-[7px] rounded-full bg-accent-cyan" />
      Loading
    </span>
    <span className="inline-flex items-center gap-1 whitespace-nowrap">
      <i className="inline-block h-[7px] w-[7px] rounded-full bg-status-error" />
      No signal
    </span>
  </div>
);

const HINT_DONE_KEY = 'tv.viewer.pinHintDone';

/** Shared browse/detail layout — map canvas + the constant right panel. */
const MapShell: React.FC<{ c: ViewerController }> = ({ c }) => {
  const isDetail = c.mode === 'detail' && c.selected != null;
  // "Click any pin" onboarding pill: once a fan has EVER opened a car
  // (this device, any event), the hint has done its job — retire it
  // instead of adding a fourth permanent floating pill to the map.
  const [hintDone, setHintDone] = React.useState<boolean>(() => {
    try {
      return localStorage.getItem(HINT_DONE_KEY) === '1';
    } catch {
      return false;
    }
  });
  React.useEffect(() => {
    if (c.selected == null || hintDone) return;
    setHintDone(true);
    try {
      localStorage.setItem(HINT_DONE_KEY, '1');
    } catch {
      /* private mode */
    }
  }, [c.selected, hintDone]);
  // Offsets for floating chrome: in browse the canvas runs full-bleed
  // UNDER the rail on lg screens, so right-anchored chrome shifts left
  // by the rail width; in detail the canvas itself is shrunk.
  const rightOffset = isDetail ? 'right-3.5' : 'right-3.5 lg:right-[414px]';
  return (
    <div className="flex min-h-0 flex-1 flex-col">
    <div className="relative flex-1 overflow-hidden">
      <div
        className={cn(
          'absolute inset-y-0 left-0 right-0 transition-[right] duration-300',
          isDetail && 'lg:right-[400px]',
        )}
      >
        {c.modules ? (
          <MapCanvas
            modules={c.modules}
            center={c.center}
            event={c.event}
            devices={c.filtered}
            replay={c.replay}
            selectedId={c.selected?.device_id ?? null}
            onSelect={(id) => c.selectCar(id)}
            lagFor={c.lagFor}
            followedIds={c.followedIds}
            // No live-session gate: the trail hook keys on
            // playbackSessionId (live → idle-device VOD fallback), so an
            // idle competitor's last-run trace draws too. The old
            // `selected?.session_id ?` guard silently blanked the big-map
            // trail for every idle pin (field bug, twice).
            trailTrack={c.trailTrack}
            trailCoverage={c.trailCoverage}
            playheadUtc={c.playheadUtc}
            onSeek={c.handleSeek}
            racelineCoords={c.racelineCoords}
            resizeKey={isDetail ? 'detail' : 'browse'}
            selectedStage={c.selectedStage}
            stageRoutes={c.stageRoutes}
            onSelectStage={c.setStage}
          />
        ) : (
          <div className="flex h-full items-center justify-center font-mono text-sm text-fg-secondary">
            loading map…
          </div>
        )}
        <ModeToggle
          mode={isDetail ? 'detail' : 'browse'}
          hasCar={c.selected != null}
          onBrowse={c.goBrowse}
          onTheater={c.goTheater}
          className={cn('absolute top-3.5 z-[1000]', rightOffset)}
        />
        {/* Stage strip moved into the EventHeader center slot on lg
            (BrowseStateView) — one less floating pill over the map. */}
        <FilterStrip
          filter={c.filter}
          onChange={c.setFilter}
          total={c.devices.length}
          live={c.liveCount}
          following={c.followedIds.size}
        />
        <MapLegend className={cn('absolute bottom-4 z-[1000] hidden md:inline-flex', rightOffset)} />
        {!isDetail && !hintDone && (
          <div
            className={cn(
              'pointer-events-none absolute bottom-16 z-[1000] hidden rounded-full border border-white/10 bg-black/70 px-3 py-1.5 text-[10px] text-fg-secondary backdrop-blur sm:block',
              rightOffset,
            )}
          >
            Click any pin to open feed
          </div>
        )}
      </div>
      <RightPanel
        active={isDetail ? 'driver' : 'cars'}
        selected={c.selected}
        onTabChange={(tab) => {
          // browse/detail: tab clicks are GLOBAL mode changes
          if (tab === 'driver') c.goDetail();
          else c.goBrowse();
        }}
        // Phones: the bottom tab bar owns Watch/Map/Cars switching.
        hideTabsOnMobile
        className={cn(
          'absolute inset-y-0 right-0 z-[1100] hidden w-[400px] border-l border-border-subtle lg:flex',
        )}
      >
        {isDetail ? <DetailMode c={c} /> : <BrowseMode c={c} />}
      </RightPanel>
    </div>
    </div>
  );
};

export const ViewerModeRouter: React.FC<{ c: ViewerController }> = ({ c }) => {
  const isDesktop = useMediaQuery(LG_MEDIA_QUERY);
  if (!isDesktop) {
    return <MobileViewer c={c} />;
  }

  // theater needs a car — fall back to the map shell until one exists
  // (e.g. a shared ?mode=theater&car=X link while X hasn't surfaced yet).
  if (c.mode === 'theater' && c.selected) {
    return <TheaterMode c={c} />;
  }
  return <MapShell c={c} />;
};

export default ViewerModeRouter;
